import { PauseIcon, Undo2Icon } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import QuickTemplateSwitcher from "components/financials/modern/templates/QuickTemplateSwitcher";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import type { TemplatesEnum } from "components/financials/modern/templates/types";
import type { UseTemplatesReturnType } from "components/financials/modern/templates/hooks/useTemplates";
import type {
  IFinancialsSheetQuery,
  IFinancialsSheetQueryVariables,
} from "components/financials/modern/graphql/__generated__/FinancialsSheetQuery.generated";
import type { ApolloQueryResult } from "@apollo/client";
import { useTemplateActions } from "components/financials/modern/templates/hooks/useTemplateActions";
import useSelfie from "lib/hooks/useSelfie";

interface TemplateSheetTriggerProps {
  ownTemplates: UseTemplatesReturnType["ownTemplates"];
  ourTemplates: UseTemplatesReturnType["ourTemplates"];
  starredTemplates: UseTemplatesReturnType["starredTemplates"];
  systemTemplate: IFinancialsTemplateFieldsFragment | null;
  renderingTemplate: IFinancialsTemplateFieldsFragment | null;
  isSheetOpen: boolean;
  isSystemTemplateActive: boolean;
  viewerCanResetToOriginal: boolean;
  viewerCanPause: boolean;
  setIsSheetOpen: (value: boolean) => void;
  setSelectedTab: (tab: TemplatesEnum) => void;
  onRefetchFinancialsTemplate: (
    newVariables: Partial<IFinancialsSheetQueryVariables>,
  ) => Promise<ApolloQueryResult<IFinancialsSheetQuery>>;
  onChange?: (template: IFinancialsTemplateFieldsFragment | null) => void;
}

export default function TemplateSheetTrigger({
  ownTemplates,
  ourTemplates,
  starredTemplates,
  systemTemplate,
  renderingTemplate,
  isSheetOpen,
  isSystemTemplateActive,
  viewerCanResetToOriginal,
  viewerCanPause,
  setIsSheetOpen,
  setSelectedTab,
  onRefetchFinancialsTemplate,
  onChange,
}: TemplateSheetTriggerProps) {
  const intl = useIntl();
  const viewer = useSelfie();
  const { setAsDefaultFinancialsTemplate } = useTemplateActions();

  return (
    <div className="flex h-10 w-full flex-nowrap items-center rounded-xl sm:w-auto">
      <button
        type="button"
        disabled={ownTemplates.loading || !viewer?.isPremium}
        onClick={() => setIsSheetOpen(!isSheetOpen)}
        className={classNames(
          "relative flex h-12 w-full flex-col items-start justify-center px-2 py-1 text-slate-950 md:h-10 lg:w-40",
          "min-w-40",
          "rounded-l-xl border border-slate-200 bg-slate-50/60",
          "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
          "focus-visible:outline-none",
          "focus-visible:ring-1 focus-visible:ring-offset-1",
          "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
          "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
          "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
          "disabled:cursor-not-allowed disabled:opacity-50",
          { "dark:bg-sky-600/20": isSystemTemplateActive },
        )}
      >
        {renderingTemplate && renderingTemplate.name && (
          <strong
            className={classNames(
              "line-clamp-1 w-full whitespace-nowrap text-left text-xs font-bold tracking-wider dark:text-slate-200",
              { "dark:text-sky-300": isSystemTemplateActive },
            )}
          >
            {renderingTemplate.name}
          </strong>
        )}
        <p className="whitespace-nowrap text-xs font-medium tracking-wider text-gray-600 dark:text-slate-400">
          <FormattedMessage
            id="ZIsIUZ"
            defaultMessage="Template"
            description="Button subtitle higlighting the selected template"
          />
        </p>
      </button>

      <QuickTemplateSwitcher
        templates={starredTemplates.templates.filter(({ archivedAt }) => archivedAt === null)}
        offerableTemplates={ourTemplates.templates}
        setIsSheetOpen={setIsSheetOpen}
        setSelectedTab={setSelectedTab}
        onChange={onChange}
      />

      {viewerCanResetToOriginal && systemTemplate ? (
        <SimpleTextTooltip
          delayDuration={600}
          content={intl.formatMessage({
            defaultMessage: "Voltar para o Padrão do Sistema",
            id: "iXtuxN",
          })}
        >
          <button
            type="button"
            onClick={async () => {
              await setAsDefaultFinancialsTemplate({
                variables: {
                  objectId: systemTemplate.objectId,
                  input: {
                    platformVertical: systemTemplate.vertical,
                  },
                },
                refetchQueries: ["UserFinancialsTemplates"],
              });

              onRefetchFinancialsTemplate({
                financialsTemplate: null,
              });
            }}
            className={classNames(
              "group inline-flex",
              "relative -ml-px h-12 items-center bg-slate-50/60 p-2 px-4 text-slate-950 md:h-10 md:px-2",
              "rounded-r-xl border  border-slate-200",
              "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
              "focus-visible:outline-none",
              "focus-visible:ring-1 focus-visible:ring-offset-1",
              "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
              "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
              "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
              "disabled:cursor-not-allowed",
            )}
          >
            <Undo2Icon
              strokeWidth={2}
              absoluteStrokeWidth={true}
              className={classNames(
                "h-5",
                "w-5",

                "stroke-violet-500 dark:stroke-violet-300",
                "group-hover:opacity-80 disabled:group-hover:opacity-100",
                "dark:group-hover:stroke-white",
                "group-disabled:stroke-slate-300 dark:group-disabled:stroke-slate-400",
              )}
            />
          </button>
        </SimpleTextTooltip>
      ) : (
        <button
          type="button"
          disabled={!renderingTemplate || !viewerCanPause}
          onClick={() => {
            onChange?.(null);
          }}
          className={classNames(
            "group inline-flex",
            "relative -ml-px h-12 items-center bg-slate-50/60 p-2 px-4 text-slate-950 md:h-10 md:px-2 lg:px-2",
            "rounded-r-xl border  border-slate-200",
            "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
            "focus-visible:outline-none",
            "focus-visible:ring-1 focus-visible:ring-offset-1",
            "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
            "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
            "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
            "disabled:cursor-not-allowed",
          )}
        >
          <PauseIcon
            strokeWidth={2}
            absoluteStrokeWidth={true}
            className={classNames(
              "h-5 w-5",
              "fill-sky-500 stroke-sky-500 dark:fill-sky-300 dark:stroke-sky-300",
              "group-hover:opacity-80 disabled:group-hover:opacity-100",
              "dark:group-hover:fill-white dark:group-hover:stroke-white",
              "group-disabled:fill-slate-50 group-disabled:stroke-slate-300 dark:group-disabled:fill-slate-700 dark:group-disabled:stroke-slate-400",
            )}
          />
        </button>
      )}
    </div>
  );
}
